import { useEffect, useRef, useState } from 'react';
import { Send, X, MessageCircle, Sparkles, ArrowUpRight } from 'lucide-react';
import axios from 'axios';
import { buildSiteContext } from '../lib/siteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'smg_jarvis_session';
const SESSION_KEY = 'smg_jarvis_session_id';

// Original animated AI orb — deep gradient core, rotating scan ring,
// pulsing central node. All SVG + CSS, no external assets.
function JarvisOrb() {
  return (
    <span className="w-full h-full relative flex items-center justify-center rounded-full overflow-hidden"
      style={{
        background: 'radial-gradient(circle at 50% 45%, #2A3562 0%, #0B1128 50%, #03050D 100%)',
      }}>
      {/* Ambient inner glow */}
      <span className="absolute inset-[8%] rounded-full pointer-events-none"
        style={{ boxShadow: 'inset 0 0 24px 4px rgba(255,61,46,0.55), 0 0 32px 6px rgba(255,61,46,0.35)' }} />
      {/* Outer rotating dashed ring */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[orbSpin_5s_linear_infinite]">
        <circle cx="50" cy="50" r="42" fill="none" stroke="#FF3D2E" strokeWidth="1.4"
          strokeDasharray="3 2" opacity="0.95" />
      </svg>
      {/* Mid ring — solid arc segments */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[orbSpinRev_7s_linear_infinite]">
        <circle cx="50" cy="50" r="34" fill="none" stroke="#FFD48A" strokeWidth="0.9"
          strokeDasharray="8 4 2 4" opacity="0.7" />
      </svg>
      {/* Inner tight ring */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[orbSpin_9s_linear_infinite]">
        <circle cx="50" cy="50" r="24" fill="none" stroke="#FFEED8" strokeWidth="0.6"
          strokeDasharray="1 3" opacity="0.55" />
      </svg>
      {/* Orbiting satellite dots */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full animate-[orbSpin_3s_linear_infinite]">
        <circle cx="50" cy="8" r="1.8" fill="#FF3D2E">
          <animate attributeName="opacity" values="1;0.4;1" dur="1.2s" repeatCount="indefinite" />
        </circle>
        <circle cx="92" cy="50" r="1.4" fill="#FFD48A" opacity="0.9" />
        <circle cx="50" cy="92" r="1" fill="#FF3D2E" opacity="0.7" />
      </svg>
      {/* Horizontal HUD line */}
      <span className="absolute left-[15%] right-[15%] top-1/2 h-px pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent 0%, rgba(255,61,46,0.6) 50%, transparent 100%)' }} />
      {/* Central pulsing core */}
      <span className="relative flex items-center justify-center z-10">
        <span className="absolute w-8 h-8 rounded-full blur-lg animate-[corePulse_2.2s_ease-in-out_infinite]"
          style={{ background: 'radial-gradient(circle, rgba(255,61,46,0.95), transparent 65%)' }} />
        <span className="absolute w-4 h-4 rounded-full blur-sm animate-[corePulse_2.2s_ease-in-out_infinite]"
          style={{ background: 'radial-gradient(circle, rgba(255,220,150,0.9), transparent 70%)' }} />
        <span className="relative w-[3px] h-[3px] rounded-full animate-[corePulse_2.2s_ease-in-out_infinite]"
          style={{ background: '#FFFFFF', boxShadow: '0 0 10px 3px #FF3D2E' }} />
      </span>
      {/* Sweeping scan */}
      <span className="absolute inset-0 rounded-full animate-[scanSweep_3s_linear_infinite] pointer-events-none"
        style={{
          background: 'conic-gradient(from 0deg, transparent 0deg, rgba(255,61,46,0.35) 25deg, rgba(255,212,138,0.15) 45deg, transparent 65deg)',
          mixBlendMode: 'screen',
        }} />
      <style>{`
        @keyframes orbSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes orbSpinRev { from { transform: rotate(360deg); } to { transform: rotate(0deg); } }
        @keyframes corePulse { 0%,100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.4); opacity: 0.8; } }
        @keyframes scanSweep { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </span>
  );
}

const STARTERS = [
  'What does SMG actually do?',
  'Who are your clients?',
  "What's a co-brand program?",
  'Pricing & engagement model?',
  'Where are you based?',
  'Can I work directly with Brian?',
];

function genSessionId() {
  return 'smg-' + Math.random().toString(36).slice(2, 12) + '-' + Date.now().toString(36);
}

export default function Jarvis() {
  const [open, setOpen] = useState(false);
  const [showTease, setShowTease] = useState(false);
  const [teaseSeen, setTeaseSeen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    try {
      const cached = localStorage.getItem(SESSION_KEY);
      if (cached) return cached;
      const id = genSessionId();
      localStorage.setItem(SESSION_KEY, id);
      return id;
    } catch {
      return genSessionId();
    }
  });
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // Hydrate messages
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      if (Array.isArray(saved)) setMessages(saved);
    } catch {}
  }, []);

  // Persist messages
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch {}
  }, [messages]);

  // 8s teaser pulse — only first time per session
  useEffect(() => {
    if (teaseSeen || open) return;
    const t = setTimeout(() => {
      setShowTease(true);
      // Auto-dismiss the teaser bubble after ~7s
      setTimeout(() => setShowTease(false), 7000);
    }, 8000);
    return () => clearTimeout(t);
  }, [teaseSeen, open]);

  // Auto-scroll to bottom on new message
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending, open]);

  // Focus input when panel opens
  useEffect(() => {
    if (open) {
      setShowTease(false);
      setTeaseSeen(true);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  const send = async (text) => {
    const message = (text ?? draft).trim();
    if (!message || sending) return;
    setDraft('');
    setSending(true);
    const next = [...messages, { role: 'user', content: message, t: Date.now() }];
    setMessages(next);
    try {
      const res = await axios.post(`${API}/chat/jarvis`, {
        session_id: sessionId,
        message,
        history: next.slice(-8).map(({ role, content }) => ({ role, content })),
        site_context: buildSiteContext(),
      }, { timeout: 60000 });
      const reply = res.data?.reply || "I'm here — could you say that one more time?";
      setMessages((m) => [...m, { role: 'assistant', content: reply, t: Date.now() }]);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'briefly offline';
      setMessages((m) => [
        ...m,
        {
          role: 'assistant',
          content: `Apologies — I'm ${detail}. While I'm reconnecting, you can use the "Start a Project" form or the founder drop-form in the Studio section to reach the team.`,
          t: Date.now(),
          error: true,
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const reset = () => {
    setMessages([]);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Floating action button + tease bubble */}
      <div className="fixed bottom-5 right-5 md:bottom-6 md:right-6 z-[300] flex flex-col items-end gap-3" style={{ bottom: 'max(20px, env(safe-area-inset-bottom))' }}>
        {showTease && !open && (
          <button
            onClick={() => setOpen(true)}
            className="group relative max-w-[260px] bg-black border border-white/15 text-white text-sm px-4 py-3 pr-9 rounded-md shadow-xl backdrop-blur-sm hover:border-white/40 transition-colors animate-fade-up"
          >
            <span className="block text-[10px] tracking-[0.3em] text-[#FF3D2E] mb-1">JARVIS</span>
            <span className="block text-white/90 leading-snug">
              Got a question? I'm here whenever you need.
            </span>
            <span
              onClick={(e) => { e.stopPropagation(); setShowTease(false); setTeaseSeen(true); }}
              className="absolute top-2 right-2 w-5 h-5 inline-flex items-center justify-center text-white/40 hover:text-white"
            >
              <X className="w-3.5 h-3.5" />
            </span>
            {/* Pointer tail */}
            <span className="absolute -bottom-1 right-6 w-2 h-2 rotate-45 bg-black border-r border-b border-white/15" />
          </button>
        )}

        <button
          aria-label={open ? 'Close Jarvis' : 'Open Jarvis'}
          onClick={() => setOpen((v) => !v)}
          className="relative group"
        >
          {/* Pulse ring (only while teaser is visible) */}
          {showTease && !open && (
            <>
              <span className="absolute inset-0 rounded-full bg-[#FF3D2E]/40 animate-ping" />
              <span className="absolute -inset-1 rounded-full bg-[#FF3D2E]/20 blur-md" />
            </>
          )}
          <span className="relative w-14 h-14 md:w-16 md:h-16 rounded-full flex items-center justify-center overflow-hidden border-2 border-black shadow-[0_10px_40px_-10px_rgba(255,61,46,0.7)] group-hover:scale-105 transition-transform">
            {open ? (
              <X className="w-6 h-6 text-white relative z-10" />
            ) : (
              <JarvisOrb size="lg" />
            )}
          </span>
        </button>
      </div>

      {/* Panel */}
      <div
        className={`fixed z-[290] bottom-0 right-0 md:bottom-24 md:right-6 w-full md:w-[400px] max-h-[100svh] md:max-h-[640px] h-[100svh] md:h-[640px] bg-black border-l md:border md:border-white/10 md:rounded-lg overflow-hidden flex flex-col shadow-[0_30px_80px_-20px_rgba(0,0,0,0.9)] transition-all duration-400 ease-out ${
          open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-3 pointer-events-none'
        }`}
        role="dialog"
        aria-label="Chat with Jarvis"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-white/10 bg-black">
          <span className="relative w-10 h-10 rounded-full overflow-hidden">
            <JarvisOrb />
            <span className="absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full bg-emerald-400 border border-black z-10" />
          </span>
          <div className="flex-1">
            <p className="text-white font-medium leading-tight">Jarvis</p>
            <p className="text-[10px] tracking-[0.25em] text-white/55">SMG STUDIO HOST · ONLINE</p>
          </div>
          {messages.length > 0 && (
            <button
              onClick={reset}
              className="text-[10px] tracking-[0.25em] text-white/50 hover:text-white border border-white/15 hover:border-white/40 px-2.5 py-1.5 transition-colors"
              title="Clear conversation"
            >
              CLEAR
            </button>
          )}
          <button
            onClick={() => setOpen(false)}
            className="text-white/60 hover:text-white"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
        >
          {messages.length === 0 && (
            <Welcome onPick={send} />
          )}
          {messages.map((m, i) => (
            <Bubble key={i} role={m.role} text={m.content} error={m.error} />
          ))}
          {sending && <TypingBubble />}
        </div>

        {/* Input */}
        <div className="border-t border-white/10 px-4 py-3 bg-black">
          <div className="flex items-end gap-2">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder="Ask Jarvis about SMG…"
              className="flex-1 bg-white/[0.04] border border-white/10 focus:border-[#FF3D2E] outline-none text-white placeholder:text-white/30 text-base md:text-sm px-3 py-2.5 resize-none max-h-32 transition-colors"
            />
            <button
              onClick={() => send()}
              disabled={!draft.trim() || sending}
              className="shrink-0 inline-flex items-center justify-center w-10 h-10 bg-[#FF3D2E] hover:bg-[#ff5446] text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              aria-label="Send"
            >
              <Send className="w-4 h-4" />
            </button>
          </div>
          <p className="mt-2 text-[9px] tracking-[0.3em] text-white/30 text-center">
            POWERED BY GPT-5 · JARVIS MAY MISSPEAK — VERIFY DETAILS WITH THE TEAM
          </p>
        </div>
      </div>

      <style>{`
        @keyframes fade-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fade-up { animation: fade-up 0.45s cubic-bezier(0.22, 1, 0.36, 1) both; }
      `}</style>
    </>
  );
}

function Welcome({ onPick }) {
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-3">
        <span className="w-8 h-8 rounded-full shrink-0 overflow-hidden">
          <JarvisOrb />
        </span>
        <div className="bg-white/[0.05] border border-white/10 rounded-md px-4 py-3 text-white/90 text-sm leading-relaxed max-w-[85%]">
          Welcome to SMG Media Group. I'm <span className="font-medium">Jarvis</span> — happy to walk you through what the studio does, our co-brand engine, or how to get in touch with Brian. Ask me anything.
        </div>
      </div>
      <div>
        <p className="text-[10px] tracking-[0.3em] text-white/40 mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-[#FF3D2E]" /> COMMON QUESTIONS
        </p>
        <div className="flex flex-wrap gap-2">
          {STARTERS.map((q) => (
            <button
              key={q}
              onClick={() => onPick(q)}
              className="text-left text-[12px] text-white/80 hover:text-white border border-white/15 hover:border-[#FF3D2E] bg-white/[0.02] hover:bg-[#FF3D2E]/10 px-3 py-2 transition-colors"
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Bubble({ role, text, error }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="bg-[#FF3D2E] text-white text-sm leading-relaxed px-4 py-3 rounded-md max-w-[85%] whitespace-pre-wrap">
          {text}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-full shrink-0 overflow-hidden">
        <JarvisOrb />
      </span>
      <div className={`border rounded-md px-4 py-3 text-sm leading-relaxed max-w-[85%] whitespace-pre-wrap ${
        error ? 'bg-red-500/10 border-red-500/30 text-white/90' : 'bg-white/[0.05] border-white/10 text-white/90'
      }`}>
        {text}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex items-start gap-3">
      <span className="w-8 h-8 rounded-full shrink-0 overflow-hidden">
        <JarvisOrb />
      </span>
      <div className="bg-white/[0.05] border border-white/10 rounded-md px-4 py-3 inline-flex items-center gap-1.5">
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="w-1.5 h-1.5 rounded-full bg-white/60"
            style={{
              animation: `typing 1.2s ${i * 0.15}s ease-in-out infinite`,
            }}
          />
        ))}
        <style>{`
          @keyframes typing {
            0%, 60%, 100% { opacity: 0.3; transform: translateY(0); }
            30% { opacity: 1; transform: translateY(-3px); }
          }
        `}</style>
      </div>
    </div>
  );
}
